// Excel export utilities for reports
import * as XLSX from 'xlsx';

/**
 * Export report data to Excel file
 * @param {Object} reportData - The report data object
 * @param {Object} dateFilter - The current date filter
 * @param {string} userRole - The user role
 */
export const exportReportsToExcel = (reportData, dateFilter, userRole) => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
      ['📊 REPORTE DE RECARGAS - RESUMEN'],
      [''],
      ['Período:', formatDateRange(dateFilter)],
      ['Usuario:', userRole],
      ['Generado:', new Date().toLocaleString('es-ES')],
      [''],      ['MÉTRICAS GENERALES'],
      ['Total de Recargas', reportData.totalRecharges],
      ['Total de Tickets', reportData.totalTickets],
      ['Ingresos Totales', `$${reportData.totalRevenue.toFixed(2)}`],
      [''],
      ['DISTRIBUCIÓN POR VEHÍCULO'],
      ['Ingresos Motos', `$${reportData.rechargesByType.moto.toFixed(2)}`],
      ['Ingresos Autos', `$${reportData.rechargesByType.auto.toFixed(2)}`]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');    // Sheet 2: Daily Statistics
    const dailyHeaders = ['Fecha', 'Día de la Semana', 'Recargas', 'Tickets', 'Ingresos'];
    const dailyData = [
      dailyHeaders,
      ...reportData.dailyStats.map(day => [
        new Date(day.date).toLocaleDateString('es-ES'),
        new Date(day.date).toLocaleDateString('es-ES', { weekday: 'long' }),
        day.count,
        day.tickets,
        `$${day.revenue.toFixed(2)}`
      ])
    ];

    const dailySheet = XLSX.utils.aoa_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'Estadísticas Diarias');    // Sheet 3: Top Users
    if (reportData.rechargesByUser.length > 0) {
      const usersHeaders = ['Posición', 'Usuario', 'Recargas', 'Ingresos'];
      const usersData = [
        usersHeaders,
        ...reportData.rechargesByUser.map((user, index) => [
          index + 1,
          user.user,
          user.count,
          `$${user.revenue.toFixed(2)}`
        ])
      ];

      const usersSheet = XLSX.utils.aoa_to_sheet(usersData);
      XLSX.utils.book_append_sheet(workbook, usersSheet, 'Ranking Usuarios');
    }

    // Generate filename
    const filename = generateFilename(dateFilter, userRole);

    // Export the file
    XLSX.writeFile(workbook, filename);

    return {
      success: true,
      filename,
      message: 'Reporte exportado exitosamente'
    };

  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return {
      success: false,
      message: 'Error al exportar el reporte: ' + error.message
    };
  }
};

/**
 * Format date range for display
 */
const formatDateRange = (dateFilter) => {
  switch (dateFilter.filterType) {
    case 'today':
      return 'Hoy';
    case 'yesterday':
      return 'Ayer';
    case 'lastDays':
      return `Últimos ${dateFilter.showLastDays} días`;
    case 'all':
      return 'Todos los datos históricos';
    case 'custom':
      // Use the same date parsing logic as in Reports.js to avoid timezone issues
      let startFormatted = '';
      let endFormatted = '';
      
      if (dateFilter.startDate) {
        const startParts = dateFilter.startDate.split('-');
        const startDateObj = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
        startFormatted = startDateObj.toLocaleDateString('es-ES');
      }
      
      if (dateFilter.endDate) {
        const endParts = dateFilter.endDate.split('-');
        const endDateObj = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
        endFormatted = endDateObj.toLocaleDateString('es-ES');
      }
      
      if (startFormatted && endFormatted) {
        return `${startFormatted} - ${endFormatted}`;
      } else if (startFormatted) {
        return `Desde: ${startFormatted}`;
      } else if (endFormatted) {
        return `Hasta: ${endFormatted}`;
      } else {
        return 'Período personalizado';
      }
    default:
      return 'Período personalizado';
  }
};

/**
 * Generate filename for export
 */
const generateFilename = (dateFilter, userRole) => {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 16).replace(/[:-]/g, '').replace('T', '_');
  
  let periodStr = '';
  switch (dateFilter.filterType) {
    case 'today':
      periodStr = 'Hoy';
      break;
    case 'yesterday':
      periodStr = 'Ayer';
      break;
    case 'lastDays':
      periodStr = `Ultimos${dateFilter.showLastDays}dias`;
      break;
    case 'all':
      periodStr = 'TodosLosDatos';
      break;
    case 'custom':
      // Use the same date parsing logic as in Reports.js to avoid timezone issues
      let start = '';
      let end = '';
      
      if (dateFilter.startDate) {
        const startParts = dateFilter.startDate.split('-');
        start = `${startParts[0]}${startParts[1]}${startParts[2]}`;
      }
      
      if (dateFilter.endDate) {
        const endParts = dateFilter.endDate.split('-');
        end = `${endParts[0]}${endParts[1]}${endParts[2]}`;
      }
      
      if (start && end) {
        periodStr = `${start}_${end}`;
      } else if (start) {
        periodStr = `Desde_${start}`;
      } else if (end) {
        periodStr = `Hasta_${end}`;
      } else {
        periodStr = 'Personalizado';
      }
      break;
    default:
      periodStr = 'Personalizado';
  }

  const roleStr = userRole === 'Supervisor' ? '_Supervisor' : '';
    return `Reporte_Recargas_${periodStr}${roleStr}_${timestamp}.xlsx`;
};

/**
 * Export CA list to Excel file
 * @param {Array} caUsers - Array of CA users
 * @param {string} userRole - The user role
 * @param {string} searchTerm - Current search term (optional)
 */
export const exportCAListToExcel = (caUsers, userRole, searchTerm = '') => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Sheet 1: CA List
    const headers = [
      'Nombre',
      'CA #',
      'Serial',
      'Cédula',
      'Dirección',
      'Email',
      'Zona',
      'Tipo de Vehículos',
      'Estado',
      'Balance',
      'ID Dispositivo'
    ];

    const caData = [
      headers,
      ...caUsers.map(ca => [
        ca.nombre,
        ca.numberCa,
        ca.serial,
        ca.cedula,
        ca.direccion,
        ca.email,
        ca.zona,
        ca.tipoVehiculos === 'autos_y_motos' ? 'Autos y Motos' :
        ca.tipoVehiculos === 'solo_autos' ? 'Solo Autos' :
        ca.tipoVehiculos === 'solo_motos' ? 'Solo Motos' : 
        ca.tipoVehiculos,
        ca.status,
        ca.balance,
        ca.idDispositivo
      ])
    ];

    const caSheet = XLSX.utils.aoa_to_sheet(caData);
    
    // Auto-size columns
    const colWidths = [
      { wch: 25 }, // Nombre
      { wch: 10 }, // CA #
      { wch: 15 }, // Serial
      { wch: 12 }, // Cédula
      { wch: 30 }, // Dirección
      { wch: 25 }, // Email
      { wch: 10 }, // Zona
      { wch: 18 }, // Tipo de Vehículos
      { wch: 10 }, // Estado
      { wch: 10 }, // Balance
      { wch: 15 }  // ID Dispositivo
    ];
    caSheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, caSheet, 'Lista de CAs');

    // Sheet 2: Summary
    const activeCAs = caUsers.filter(ca => ca.status === 'Activo').length;
    const inactiveCAs = caUsers.filter(ca => ca.status === 'Inactivo').length;
    const totalBalance = caUsers.reduce((sum, ca) => sum + (ca.balance || 0), 0);
    
    const summaryData = [
      ['📋 RESUMEN DE CONTROLADORES AUTORIZADOS'],
      [''],
      ['Generado:', new Date().toLocaleString('es-ES')],
      ['Usuario:', userRole],
      ['Filtro aplicado:', searchTerm || 'Ninguno'],
      [''],
      ['ESTADÍSTICAS GENERALES'],
      ['Total de CAs:', caUsers.length],
      ['CAs Activos:', activeCAs],
      ['CAs Inactivos:', inactiveCAs],
      ['Balance Total:', `$${totalBalance.toFixed(2)}`],
      [''],
      ['DISTRIBUCIÓN POR TIPO DE VEHÍCULO'],
      ['Autos y Motos:', caUsers.filter(ca => ca.tipoVehiculos === 'autos_y_motos').length],
      ['Solo Autos:', caUsers.filter(ca => ca.tipoVehiculos === 'solo_autos').length],
      ['Solo Motos:', caUsers.filter(ca => ca.tipoVehiculos === 'solo_motos').length]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

    // Generate filename
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 16).replace(/[:-]/g, '').replace('T', '_');
    const filename = `Lista_CAs_${timestamp}.xlsx`;

    // Export the file
    XLSX.writeFile(workbook, filename);

    return {
      success: true,
      filename,
      message: 'Lista de CAs exportada exitosamente'
    };

  } catch (error) {
    console.error('Error exporting CA list to Excel:', error);
    return {
      success: false,
      message: 'Error al exportar la lista: ' + error.message
    };
  }
};

const excelExportService = { exportReportsToExcel, exportCAListToExcel };
export default excelExportService;
